import ActiveResourceState from '@states/ActiveResource.js';
import UserState from '@states/User.js';
import {
  ORG_PERMISSION_RULES,
  PERSONAL_SPACE_PERMISSIONS,
  RESOURCES,
  ROLES,
  SPACES,
} from './permissions.js';

class AccessControl {
  constructor({
    subscribeToGlobal = true,
    initialResource,
    initialResourceType,
    initialContext,
  } = {}) {
    this.user = UserState.user;
    this.resource = initialResource;
    this.resourceType = initialResourceType;
    this.context = initialContext || {};
    this.subscribeToGlobal = subscribeToGlobal;

    if (subscribeToGlobal) {
      ActiveResourceState.subscribe(state => {
        this.resource = state.resource;
        this.resourceType = state.resourceType;
        this.context = state.context;
        if (state.spaceType) {
          this.context.spaceType = state.spaceType;
        }
      });

      UserState.subscribe(user => {
        this.user = user;
      });
    }
  }

  setActiveResource(resource, resourceType, context = {}) {
    if (this.subscribeToGlobal) {
      ActiveResourceState.setActiveResource({
        resource,
        resourceType,
        context,
        spaceType:
          context?.spaceType ||
          this.getSpaceTypeFromResource(resource, context),
      });
    } else {
      this.resource = resource;
      this.resourceType = resourceType;
      this.context = { ...this.context, ...context };
    }
    this.user = UserState.user;
    return this;
  }

  setResource(resource) {
    if (this.subscribeToGlobal) {
      ActiveResourceState.setActiveResource({
        ...ActiveResourceState.activeResource,
        resource,
      });
    } else {
      this.resource = resource;
    }
  }

  setResourceType(resourceType) {
    if (this.subscribeToGlobal) {
      ActiveResourceState.setActiveResource({
        ...ActiveResourceState.activeResource,
        resourceType,
      });
    } else {
      this.resourceType = resourceType;
    }
  }

  setContext(newContext) {
    if (this.subscribeToGlobal) {
      ActiveResourceState.setActiveResource({
        ...ActiveResourceState.activeResource,
        context: {
          ...ActiveResourceState.activeResource.context,
          ...newContext,
        },
      });
    } else {
      this.context = { ...this.context, ...newContext };
    }
  }

  setOverrideRole(role) {
    if (this.subscribeToGlobal) {
      const newContext = {
        ...ActiveResourceState.activeResource.context,
        overrideRole: role,
      };
      this.setContext(newContext);
    } else {
      this.context = { ...this.context, overrideRole: role };
    }
  }

  getOverrideRole() {
    return this.subscribeToGlobal
      ? ActiveResourceState.activeResource.context?.overrideRole || null
      : this.context?.overrideRole || null;
  }

  clearOverrideRole() {
    if (this.subscribeToGlobal) {
      const newContext = { ...ActiveResourceState.activeResource.context };
      delete newContext.overrideRole;
      this.setContext(newContext);
    } else {
      const newContext = { ...this.context };
      delete newContext.overrideRole;
      this.context = newContext;
    }
  }

  getSpaceType() {
    if (!this.resource) return this.context?.spaceType || SPACES.PERSONAL;
    return this.resource.organizationId
      ? SPACES.ORGANIZATION
      : this.context?.spaceType || SPACES.PERSONAL;
  }

  getSpaceTypeFromResource(resource, context) {
    if (!resource) return context?.spaceType || SPACES.PERSONAL;
    return context?.spaceType === SPACES.ORGANIZATION
      ? SPACES.ORGANIZATION
      : SPACES.PERSONAL;
  }

  getUserOrgRole() {
    if (this.context?.overrideRole) {
      return this.context?.overrideRole;
    }

    if (!this.user) {
      return ROLES.ANONYMOUS;
    }

    const { resource, user } = this;

    if (!resource?.organizationId) {
      return null;
    }

    const orgData = user.orgData;
    if (!orgData) {
      return null;
    }

    const orgMembership = orgData.find(
      org => org.organizationId === resource.organizationId,
    );

    let role = orgMembership?.role;

    if (role?.toLowerCase() === 'user') {
      role = ROLES.MEMBER;
    }

    return role || null;
  }

  hasPermission(action, additionalContext = {}) {
    const spaceType = this.getSpaceType();
    const resourceType =
      additionalContext.resourceType || this.mapResourceType();
    const context = {
      ...this.context,
      ...additionalContext,
      spaceType,
    };
    if (!context.overrideRole) {
      context.overrideRole = this.resource?.accessType;
    }
    return this.checkPermission(resourceType, action, context, spaceType);
  }

  checkPermission(resourceType, action, context, spaceType) {
    if (context.overrideRole?.toLowerCase() === 'edit') {
      context.overrideRole = 'admin';
    } else if (context.overrideRole?.toLowerCase() === 'view') {
      context.overrideRole = 'member';
    }
    let isOwner = false;
    if (this.user) {
      isOwner = this.resource?.uid === this.user?.uid;
    }
    let userRole;
    if (this.user) {
      userRole =
        context.overrideRole || this.getUserOrgRole() || ROLES.ANONYMOUS;
    } else {
      userRole = ROLES.ANONYMOUS;
    }
    const role = isOwner ? ROLES.ADMIN : userRole;
    const permissionRules =
      spaceType === SPACES.PERSONAL
        ? PERSONAL_SPACE_PERMISSIONS
        : ORG_PERMISSION_RULES;

    let roleKey =
      Object.keys(ROLES).find(
        key => ROLES[key].toLowerCase() === String(role).toLowerCase(),
      ) || String(role).toUpperCase();
    const rulesKeys = Object.keys(permissionRules);
    const matchCase = rulesKeys.some(k => k === k.toUpperCase())
      ? 'toUpperCase'
      : 'toLowerCase';
    roleKey = roleKey[matchCase]();

    const rolePermissions =
      permissionRules[roleKey] || permissionRules[roleKey[matchCase]()] || {};
    return rolePermissions[resourceType]?.[action] instanceof Function
      ? rolePermissions[resourceType][action](this.user, this.resource, context)
      : !!rolePermissions[resourceType]?.[action];
  }

  mapResourceType() {
    switch (this.resourceType) {
      case 'project':
        return RESOURCES.PROJECT;
      case 'task':
        return RESOURCES.TASK;
      case 'comment':
        return RESOURCES.COMMENT;
      case 'report':
        return RESOURCES.REPORT;
      case 'folder':
        return RESOURCES.FOLDER;
      case 'quotation':
        return RESOURCES.QUOTATION;
      case 'ai-analyser':
        return RESOURCES.AI_ANALYSER;
      default:
        return this.resourceType;
    }
  }
}
// singleton instance
const accessControlInstance = new AccessControl();
export { AccessControl };
export default accessControlInstance;
